<template>
    <div class="relative linkeate-user-menu" @pointerenter="handlePointerEnter" @pointerleave="handlePointerLeave"
        @focusin="openMenu" @focusout="handleFocusOut">
        <button type="button" class="hdr_profile_btn linkeate-user-menu__trigger" :class="{ 'is-open': open }"
            aria-haspopup="true" :aria-expanded="open ? 'true' : 'false'" :aria-label="triggerLabel"
            @click.prevent="toggleMenu">
            <span class="linkeate-user-menu__icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path
                        d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z"
                        stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M4 20C4.86239 16.5597 8.10853 14 12 14C15.8915 14 19.1376 16.5597 20 20"
                        stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
            </span>
            <span class="linkeate-user-menu__chevron">
                <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"
                        stroke-linejoin="round" />
                </svg>
            </span>
        </button>

        <transition name="linkeate-user-menu-fade">
            <div v-if="open" class="linkeate-user-menu__panel absolute z-50 top-full right-0 mt-3" @click.stop @mouseenter="clearCloseTimer" @mouseleave="scheduleClose">
                <div v-if="authenticated" class="linkeate-user-menu__card">
                    <div class="linkeate-user-menu__header">
                        <div v-if="user.avatar" class="linkeate-user-menu__avatar">
                            <img :src="user.avatar" :alt="user.fullName || 'Usuario'" />
                        </div>
                        <div v-else class="linkeate-user-menu__avatar linkeate-user-menu__avatar--fallback">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path
                                    d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z"
                                    stroke="currentColor" stroke-width="1.8" stroke-linecap="round"
                                    stroke-linejoin="round" />
                                <path d="M4 20C4.86239 16.5597 8.10853 14 12 14C15.8915 14 19.1376 16.5597 20 20"
                                    stroke="currentColor" stroke-width="1.8" stroke-linecap="round"
                                    stroke-linejoin="round" />
                            </svg>
                        </div>

                        <div class="linkeate-user-menu__identity">
                            <p class="linkeate-user-menu__eyebrow">Usuario conectado</p>
                            <h4 class="linkeate-user-menu__name">{{ user.fullName || user.name || 'Usuario' }}</h4>
                            <p class="linkeate-user-menu__email">{{ user.email }}</p>
                        </div>
                    </div>

                    <div class="linkeate-user-menu__details">
                        <div class="linkeate-user-menu__row">
                            <span class="linkeate-user-menu__label">Nombre</span>
                            <span class="linkeate-user-menu__value">{{ user.name || 'No registrado' }}</span>
                        </div>
                        <div class="linkeate-user-menu__row">
                            <span class="linkeate-user-menu__label">Apellido</span>
                            <span class="linkeate-user-menu__value">{{ user.lastName || 'No registrado' }}</span>
                        </div>
                        <div class="linkeate-user-menu__row">
                            <span class="linkeate-user-menu__label">Correo</span>
                            <span class="linkeate-user-menu__value linkeate-user-menu__value--break">{{ user.email }}</span>
                        </div>
                        <div class="linkeate-user-menu__row">
                            <span class="linkeate-user-menu__label">Teléfono</span>
                            <span class="linkeate-user-menu__value">{{ user.phone || 'No registrado' }}</span>
                        </div>
                    </div>

                    <div class="linkeate-user-menu__actions">
                        <a :href="links.dashboard" class="linkeate-user-menu__action">
                            <i class="mdi mdi-view-dashboard-outline" aria-hidden="true"></i>
                            Dashboard
                        </a>
                        <a :href="links.profile" class="linkeate-user-menu__action">
                            <i class="mdi mdi-account-cog-outline" aria-hidden="true"></i>
                            Mi perfil
                        </a>
                    </div>

                    <form class="linkeate-user-menu__logout-wrap" method="POST" :action="links.logout">
                        <input type="hidden" name="_token" :value="csrfToken">
                        <button type="submit" class="linkeate-user-menu__logout">
                            <i class="mdi mdi-logout" aria-hidden="true"></i>
                            Cerrar sesión
                        </button>
                    </form>
                </div>

                <div v-else class="linkeate-user-menu__card linkeate-user-menu__card--guest">
                    <p class="linkeate-user-menu__eyebrow">Tu cuenta</p>
                    <h4 class="linkeate-user-menu__name">Accede a Linkeate</h4>
                    <p class="linkeate-user-menu__guest-copy">
                        Inicia sesión o crea tu cuenta para guardar favoritos, comprar y gestionar tu perfil.
                    </p>

                    <div class="linkeate-user-menu__guest-actions">
                        <a :href="links.login" class="linkeate-user-menu__primary-link">
                            <i class="mdi mdi-login" aria-hidden="true"></i>
                            Iniciar sesión
                        </a>
                        <a :href="links.register" class="linkeate-user-menu__secondary-link">
                            <i class="mdi mdi-account-plus-outline" aria-hidden="true"></i>
                            Crear cuenta
                        </a>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
export default {
    name: 'MenuUsuario',
    data() {
        const config = window.__menuUsuario || {};
        return {
            open: false,
            closeTimer: null,
            authenticated: config.authenticated || false,
            user: config.user || {},
            links: config.links || {},
            csrfToken: config.csrfToken || '',
        };
    },
    computed: {
        triggerLabel() {
            return this.authenticated ? 'Abrir menú de usuario' : 'Abrir opciones de acceso';
        },
    },
    mounted() {
        document.addEventListener('click', this.handleDocumentClick);
        document.addEventListener('keydown', this.handleDocumentKeydown);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleDocumentClick);
        document.removeEventListener('keydown', this.handleDocumentKeydown);
        this.clearCloseTimer();
    },
    methods: {
        openMenu() {
            this.clearCloseTimer();
            this.open = true;
        },
        closeMenu() {
            this.open = false;
        },
        scheduleClose() {
            this.clearCloseTimer();
            this.closeTimer = setTimeout(() => {
                this.open = false;
            }, 200);
        },
        clearCloseTimer() {
            if (this.closeTimer) {
                clearTimeout(this.closeTimer);
                this.closeTimer = null;
            }
        },
        toggleMenu() {
            this.open = !this.open;
        },
        handlePointerEnter(event) {
            if (event.pointerType !== 'mouse') return;
            this.openMenu();
        },
        handlePointerLeave(event) {
            if (event.pointerType !== 'mouse') return;
            this.scheduleClose();
        },
        handleFocusOut(event) {
            if (!event.relatedTarget || !this.$el.contains(event.relatedTarget)) {
                this.scheduleClose();
            }
        },
        handleDocumentClick(event) {
            if (!this.$el.contains(event.target)) {
                this.closeMenu();
            }
        },
        handleDocumentKeydown(event) {
            if (event.key === 'Escape') {
                this.closeMenu();
            }
        },
    },
};
</script>

<style scoped>
.linkeate-user-menu__trigger {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    min-height: 48px;
    padding: 0.5rem 0.875rem;
    border: 1px solid #e5e7eb;
    border-radius: 5px;
    background: #f5f5f5;
    color: #000;
    transition: all 0.2s ease;
}

.linkeate-user-menu__trigger:hover,
.linkeate-user-menu__trigger.is-open {
    background: #fff;
    border-color: #089cde;
    color: #089cde;
    box-shadow: 0 10px 30px rgba(8, 156, 222, 0.12);
}

.linkeate-user-menu__icon,
.linkeate-user-menu__chevron {
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.linkeate-user-menu__icon svg {
    width: 22px;
    height: 22px;
}

.linkeate-user-menu__chevron svg {
    width: 18px;
    height: 18px;
    transition: transform 0.2s ease;
}

.linkeate-user-menu__trigger.is-open .linkeate-user-menu__chevron svg {
    transform: rotate(180deg);
}

.linkeate-user-menu__panel {
    width: 320px;
}

.linkeate-user-menu__card {
    border: 1px solid #e2e8f0;
    border-radius: 5px;
    background: #fff;
    padding: 1.25rem;
    box-shadow: 0 24px 60px rgba(15, 23, 42, 0.14);
}

.linkeate-user-menu__card--guest {
    width: 280px;
}

.linkeate-user-menu__header {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #f1f5f9;
}

.linkeate-user-menu__avatar {
    width: 56px;
    height: 56px;
    border-radius: 5px;
    overflow: hidden;
    border: 1px solid #e2e8f0;
    flex-shrink: 0;
}

.linkeate-user-menu__avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.linkeate-user-menu__avatar--fallback {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #f5f5f5;
    color: #0f172a;
}

.linkeate-user-menu__avatar--fallback svg {
    width: 28px;
    height: 28px;
}

.linkeate-user-menu__identity {
    min-width: 0;
}

.linkeate-user-menu__eyebrow {
    margin: 0;
    color: #089cde;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.24em;
    text-transform: uppercase;
}

.linkeate-user-menu__name {
    margin: 0.25rem 0 0;
    color: #0f172a;
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.2;
    word-break: break-word;
}

.linkeate-user-menu__email {
    margin: 0.25rem 0 0;
    color: #64748b;
    font-size: 0.875rem;
    line-height: 1.45;
    word-break: break-all;
}

.linkeate-user-menu__details {
    display: grid;
    gap: 0.75rem;
    padding: 1rem 0;
}

.linkeate-user-menu__row {
    display: grid;
    grid-template-columns: 88px minmax(0, 1fr);
    gap: 0.75rem;
    align-items: start;
}

.linkeate-user-menu__label {
    color: #64748b;
    font-size: 0.875rem;
    font-weight: 500;
}

.linkeate-user-menu__value {
    color: #000;
    font-size: 0.875rem;
    font-weight: 600;
    text-align: right;
}

.linkeate-user-menu__value--break {
    word-break: break-all;
}

.linkeate-user-menu__actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
    padding-top: 1rem;
    border-top: 1px solid #f1f5f9;
}

.linkeate-user-menu__action,
.linkeate-user-menu__primary-link,
.linkeate-user-menu__secondary-link,
.linkeate-user-menu__logout {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    width: 100%;
    min-height: 44px;
    border-radius: 5px;
    font-size: 0.875rem;
    font-weight: 600;
    transition: all 0.2s ease;
}

.linkeate-user-menu__action {
    border: 1px solid #e2e8f0;
    background: #fff;
    color: #000;
}

.linkeate-user-menu__action:hover {
    border-color: #089cde;
    color: #089cde;
}

.linkeate-user-menu__logout-wrap {
    margin-top: 0.75rem;
}

.linkeate-user-menu__logout {
    border: 1px solid #fee2e2;
    background: #fff5f5;
    color: #b91c1c;
}

.linkeate-user-menu__logout:hover {
    background: #fee2e2;
}

.linkeate-user-menu__guest-copy {
    margin: 0.75rem 0 0;
    color: #64748b;
    font-size: 0.875rem;
    line-height: 1.65;
}

.linkeate-user-menu__guest-actions {
    display: grid;
    gap: 0.75rem;
    margin-top: 1rem;
}

.linkeate-user-menu__primary-link {
    background: #089cde;
    color: #fff;
}

.linkeate-user-menu__primary-link:hover {
    background: #067cb0;
    color: #fff;
}

.linkeate-user-menu__secondary-link {
    border: 1px solid #dbe4ea;
    background: #fff;
    color: #000;
}

.linkeate-user-menu__secondary-link:hover {
    border-color: #089cde;
    color: #089cde;
}

.linkeate-user-menu-fade-enter-active,
.linkeate-user-menu-fade-leave-active {
    transition: opacity 0.18s ease, transform 0.18s ease;
}

.linkeate-user-menu-fade-enter-from,
.linkeate-user-menu-fade-leave-to {
    opacity: 0;
    transform: translateY(10px);
}

@media (max-width: 640px) {
    .linkeate-user-menu__panel,
    .linkeate-user-menu__card--guest {
        width: min(300px, calc(100vw - 24px));
    }

    .linkeate-user-menu__row {
        grid-template-columns: 1fr;
        gap: 0.25rem;
    }

    .linkeate-user-menu__value {
        text-align: left;
    }

    .linkeate-user-menu__actions {
        grid-template-columns: 1fr;
    }
}
</style>
